import React, { useState } from 'react';
import Api from 'api';
import Container from 'components/UI/Container';
import Title from 'components/UI/Title';
import Tabs from 'components/UI/Tabs';
import Content from './Content';
import InternalEvents from './InternalEvents';
import { createPanelID, createTabID } from 'utils/tabs';
import { getCountryCode, useLang, useTrans } from 'utils/localisation';
import { useDefferedCallback } from 'utils/hooks';
import { withErrorBoundary } from 'components/ErrorBoundary';
import landings from './landings.json';

import * as styles from './Events.module.scss';

const getTabItem = (id, cards, loading, eventsPage) => {
  if (id === 'internalEvents') {
    return <InternalEvents cards={landings} eventsPage={eventsPage} />;
  }
  return <Content cards={cards} loading={loading} eventsPage={eventsPage} />;
};

function Events({ title, eventPageSlug, initialTab = 'internalEvents', eventsPage = false }) {
  const t = useTrans(['events']);
  const lang = useLang();
  const tabsAriaLabel = 'Events Tabs';
  const [active, setActive] = useState(initialTab);
  const [opacity, setOpacity] = useState({
    '--opacity': 1,
  });
  const tabs = [
    {
      id: 'internalEvents',
      tabLabel: t('internalEvents'),
    },
    {
      id: 'externalEvents',
      tabLabel: t('externalEvents'),
    },
  ];

  const [loading, setLoading] = useState(true);
  const [cards, setCards] = useState(null);

  const fetchData = () => {
    Api.getEvents(lang, getCountryCode()?.toLowerCase())
      .then(data => {
        if (eventPageSlug) {
          setCards(data.filter(event => !event.url?.includes(eventPageSlug)));
        } else {
          setCards(data);
        }
      })
      .finally(() => setLoading(false));
  };

  const { inViewRef } = useDefferedCallback(fetchData, {
    rootMargin: '100% 0% 100% 0%',
    delay: 9000,
  });

  return (
    <Container id="events" background="gray">
      <div className={styles.layout} ref={inViewRef}>
        {!eventsPage && <Title className={styles.title}>{title ?? t('title')}</Title>}
        <Tabs
          filters={tabs}
          active={active}
          setActive={setActive}
          setStyles={setOpacity}
          tabsAriaLabel={tabsAriaLabel}
        />
        {tabs.map(({ id }, i) => (
          <section
            key={id}
            id={createPanelID(tabsAriaLabel, i)}
            className={styles.section}
            style={opacity}
            role="tabpanel"
            tabIndex={0}
            aria-labelledby={createTabID(tabsAriaLabel, i)}
            hidden={active !== id}
          >
            {getTabItem(id, cards, loading, eventsPage)}
          </section>
        ))}
      </div>
    </Container>
  );
}

Events.displayName = 'Events';

export default withErrorBoundary(Events);
